import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { RefreshCw, ArrowDownLeft, ArrowUpRight, FileText } from 'lucide-react';
import { getEdiTransactions } from '../../config/api';

const STATUS_BADGE = (s) => {
  const u = String(s || '').toUpperCase();
  if (['POSTED', 'PROCESSED', 'COMPLETED', 'SENT', 'VALIDATED'].includes(u)) return 'bg-green-100 text-green-800';
  if (['FAILED', 'IN_ERROR', 'ERROR', 'REJECTED'].includes(u)) return 'bg-red-100 text-red-700';
  if (['VALIDATING', 'PROCESSING', 'PENDING', 'IMPORTED'].includes(u)) return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-700';
};

const EDITransactions = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState('');
  const [docType, setDocType] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await getEdiTransactions({ direction, docType, search });
      setRows(data.transactions || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error('Failed to load EDI transactions'); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [direction, docType, search]);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-medium text-gray-900">EDI Transactions</h3>
          <p className="text-sm text-gray-500">Every inbound file, processing batch, and outbound notification — {total} transactions.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search reference…" className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
          <select value={direction} onChange={e => setDirection(e.target.value)} className="border border-gray-300 rounded-md px-2 py-2 text-sm">
            <option value="">All directions</option><option value="Inbound">Inbound</option><option value="Outbound">Outbound</option>
          </select>
          <select value={docType} onChange={e => setDocType(e.target.value)} className="border border-gray-300 rounded-md px-2 py-2 text-sm">
            <option value="">All types</option>{['844', '845', '849', 'EXCEL', 'MANUAL'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button onClick={load} title="Refresh" className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50"><tr>
            {['Direction', 'Type', 'Event', 'Reference', 'Records', 'Errors', 'Status', 'When'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>)}
          </tr></thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500"><FileText className="h-8 w-8 mx-auto text-gray-300 mb-2" />No EDI transactions yet.</td></tr>
            ) : rows.map(t => (
              <tr key={t.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm">
                  <span className={`inline-flex items-center gap-1 ${t.direction === 'Inbound' ? 'text-blue-700' : 'text-purple-700'}`}>
                    {t.direction === 'Inbound' ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}{t.direction}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">EDI {t.docType}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{t.kind}</td>
                <td className="px-4 py-3 text-sm text-gray-700 font-mono">{t.reference || '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{t.records ?? '—'}</td>
                <td className={`px-4 py-3 text-sm ${t.errors ? 'text-red-600 font-medium' : 'text-gray-400'}`}>{t.errors ?? '—'}</td>
                <td className="px-4 py-3 text-sm"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE(t.status)}`}>{t.status}</span></td>
                <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{t.at ? new Date(t.at).toLocaleString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EDITransactions;
